// src/selectors.ts
var Selectors = {
  Cell: '[role="cell"],[role="gridcell"],[role="columnheader"],[role="rowheader"],td,th',
  Row: '[role="row"],tr',
  RowGroup: '[role="rowgroup"],thead,tbody,tfoot',
  /**
   * Selector from here: https://github.com/Shopify/polaris/blob/main/polaris-react/src/utilities/focus.ts#L10
   *
   * Plus two design-system controls whose host is not focusable by tag or by a
   * `tabindex` attribute. `ps-select` (PSTUDIO-4784): its text field lives in
   * its shadow root and its `focus()` delegates there, so without this entry
   * Enter on a cell holding one finds nothing to step into and focus stays on
   * the td. `ps-select` reflects `disabled`, hence the `:not([disabled])`.
   */
  Focusable: 'ps-checkbox,ps-select:not([disabled]),a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not([aria-disabled="true"]):not([tabindex="-1"]):not(:disabled),*[tabindex]'
};

// src/use-table-nav.ts
function useTableNav(options) {
  const nav = new DataGridNav(options);
  return {
    listeners: {
      onKeyDown: (e) => nav.tableKeyDown(e),
      onKeyUp: () => nav.tableKeyUp()
    },
    tableNav: nav
  };
}

// src/index.ts
var DataGridNav = class {
  constructor(config = {}) {
    this.keys = [];
    /** Whether a window-level once keyup listener is currently armed (see tableKeyDown). */
    this.windowKeyUpArmed = false;
    this.debugLog = (functionName, message) => {
      if (this.debug) console.info(`[${functionName}]: ${message}`);
    };
    const { selectors = {}, pageUpDown, debug = false } = config;
    this.selectors = { ...Selectors, ...selectors };
    this.pageUpDown = pageUpDown;
    this.keys = [];
    this.debug = debug;
    this.disabled = false;
  }
  /**
   * Disables the keyboard listener in cases
   * that elements inside the grid need to use
   * arrows keys etc., like select dropdowns
   */
  disable() {
    this.disabled = true;
  }
  /**
   * Enables the keyboard listeners
   */
  enable() {
    this.disabled = false;
  }
  isFocusable(el) {
    return el instanceof HTMLElement || el instanceof SVGElement;
  }
  /** Check if cell is hidden (has data-hidden="true") - such cells should be skipped during navigation */
  isHiddenCell(el) {
    return el.getAttribute("data-hidden") === "true";
  }
  /**
   * Check if an element is excluded from keyboard navigation
   * (has data-nav-skip="true") — e.g. node-group header rows that render a
   * single colspan td with no role="gridcell" cells, so focus would
   * silently die on them. Checked inside findUntil for both row and cell
   * walks; by contract the attribute is only ever set on rows.
   */
  isNavSkipped(el) {
    return el.getAttribute("data-nav-skip") === "true";
  }
  /**
   * Check if a cell is disabled for its row's node type
   * (data-disabled="true") — pro-studio's cleared-zone tds: empty, greyed,
   * painted as one merged strip. Horizontal navigation (arrows / Tab) skips
   * them so one keypress hops the whole zone instead of walking focus
   * invisibly through each dead cell (PSTUDIO-2372). By contract the
   * attribute is only ever set on cells, so row walks are unaffected.
   */
  isDisabledCell(el) {
    return el.getAttribute("data-disabled") === "true";
  }
  /**
   * Get visible cell from row, preferring the one at preferredIndex.
   * If that cell is hidden, finds the nearest visible cell.
   */
  getVisibleCellFromRow(row, preferredIndex) {
    const { children } = row;
    const child = children[preferredIndex];
    if (child && !this.isHiddenCell(child) && this.isFocusable(child)) {
      return child;
    }
    for (let i = 1; i < children.length; i++) {
      const rightIdx = preferredIndex + i;
      const leftIdx = preferredIndex - i;
      if (rightIdx < children.length) {
        const rightChild = children[rightIdx];
        if (rightChild && !this.isHiddenCell(rightChild) && this.isFocusable(rightChild)) {
          return rightChild;
        }
      }
      if (leftIdx >= 0) {
        const leftChild = children[leftIdx];
        if (leftChild && !this.isHiddenCell(leftChild) && this.isFocusable(leftChild)) {
          return leftChild;
        }
      }
    }
    return null;
  }
  /**
   * Focuses the visible cell at preferredIndex (or the nearest visible one)
   * in the given row, scrolling it into view if needed.
   */
  focusCellInRow(row, preferredIndex) {
    const targetCell = this.getVisibleCellFromRow(row, preferredIndex);
    if (targetCell && this.isFocusable(targetCell)) {
      targetCell.focus();
      this.scrollToCellIfNeeded(targetCell);
    }
  }
  /**
   * Returns the closest navigable row of the adjacent rowgroup
   * (first row when moving next, last row when moving prev),
   * skipping rows marked data-nav-skip="true". Null when there is no
   * adjacent rowgroup or it has no navigable rows.
   */
  getRowInAdjacentRowGroup(row, direction) {
    const currentRowGroup = row.parentElement?.closest(this.selectors.RowGroup);
    const siblingRowGroups = [
      ...currentRowGroup?.parentElement?.children || []
    ];
    const currentRowGroupIdx = siblingRowGroups.findIndex(
      (el) => el === currentRowGroup
    );
    if (currentRowGroupIdx === -1) return null;
    const adjacentIdx = direction === "next" ? currentRowGroupIdx + 1 : currentRowGroupIdx - 1;
    if (adjacentIdx < 0 || adjacentIdx >= siblingRowGroups.length) return null;
    const rows = [
      ...siblingRowGroups[adjacentIdx].querySelectorAll(this.selectors.Row)
    ].filter((rowEl) => !this.isNavSkipped(rowEl));
    if (rows.length === 0) return null;
    return direction === "next" ? rows[0] : rows[rows.length - 1];
  }
  /** Used as a keyboard listener for key up */
  tableKeyUp() {
    this.keys = [];
  }
  /** Used as a keyboard listener for key down */
  tableKeyDown(e) {
    this.debugLog("tableKeyDown", `Key pressed: ${e.key}`);
    if (this.disabled) {
      this.debugLog("tableKeyDown", "interaction is disabled");
      return;
    }
    if ("ArrowDown" /* ArrowDown */ === e.key || "ArrowUp" /* ArrowUp */ === e.key || "ArrowLeft" /* ArrowLeft */ === e.key || "ArrowRight" /* ArrowRight */ === e.key) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        this.debugLog(
          "tableKeyDown",
          "target is input element so not preventing default"
        );
      } else {
        e.preventDefault();
      }
    }
    if (this.keys.length === 0 || this.keys[this.keys.length - 1] !== e.key) {
      this.keys.push(e.key);
    }
    if (typeof window !== "undefined" && !this.windowKeyUpArmed) {
      this.windowKeyUpArmed = true;
      window.addEventListener(
        "keyup",
        () => {
          this.windowKeyUpArmed = false;
          this.keys = [];
        },
        { once: true }
      );
    }
    if (!(e.target instanceof Element)) return;
    const cell = e.target.parentElement?.closest(
      `${this.selectors.Cell},${this.selectors.Row}`
    );
    if (!cell) {
      this.debugLog("tableKeyDown", "cell not found");
      return;
    }
    if (cell.matches(this.selectors.Cell)) {
      this.debugLog("tableKeyDown", "event captured in cell");
      this.debugLog("cellNavigation", "event captured in cell");
      this.cellNavigation(e);
    } else {
      this.debugLog("tableKeyDown", "event captured in cell");
      this.debugLog("gridNavigation", "event captured in cell");
      this.gridNavigation(e);
    }
  }
  /**
   * Handles the navigation inside a cell
   */
  cellNavigation(e) {
    if (!(e.target instanceof Element)) return;
    if (e.key === "Escape" /* Escape */) {
      const cell = e.target.closest(this.selectors.Cell);
      if (cell && this.isFocusable(cell)) {
        cell.focus();
        return;
      }
    }
    if (e.key === "Tab" /* Tab */) {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      if (el.closest('[role="listbox"]') || el.getAttribute("role") === "option" || el.tagName?.toLowerCase() === "ps-select-option") {
        return;
      }
      const cell = el.closest(this.selectors.Cell);
      if (!cell || !this.isFocusable(cell)) return;
      const direction = e.shiftKey ? "prev" : "next";
      const nextCell = this.findUntil(direction, cell, this.selectors.Cell);
      if (nextCell && this.isFocusable(nextCell)) {
        e.preventDefault();
        const active = document.activeElement;
        if (active && typeof active.blur === "function") {
          active.blur();
        }
        requestAnimationFrame(() => {
          nextCell.focus();
          this.scrollToCellIfNeeded(nextCell);
        });
      }
      return;
    }
    if (e.key === "Enter" /* Enter */ && !e.shiftKey) {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      if (el.closest('[role="listbox"]') || el.getAttribute("role") === "option" || el.tagName?.toLowerCase() === "ps-select-option") {
        return;
      }
      const cell = el.closest(this.selectors.Cell);
      if (!cell || !this.isFocusable(cell)) return;
      const row = cell.closest(this.selectors.Row);
      if (!row) return;
      const cellPosition = this.getColumnIndex(cell);
      if (cellPosition === void 0) return;
      const nextRow = this.getNextRow(row);
      const destinationCell = nextRow ? this.getVisibleCellFromRow(nextRow, cellPosition) : cell;
      if (destinationCell && this.isFocusable(destinationCell)) {
        destinationCell.focus();
        this.scrollToCellIfNeeded(destinationCell);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "Enter" /* Enter */ && e.shiftKey) {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      if (el.closest('[role="listbox"]') || el.getAttribute("role") === "option" || el.tagName?.toLowerCase() === "ps-select-option") {
        return;
      }
      const cell = el.closest(this.selectors.Cell);
      if (!cell || !this.isFocusable(cell)) return;
      const row = cell.closest(this.selectors.Row);
      if (!row) return;
      const cellPosition = this.getColumnIndex(cell);
      if (cellPosition === void 0) return;
      const prevRow = this.getPreviousRow(row);
      const destinationCell = prevRow ? this.getVisibleCellFromRow(prevRow, cellPosition) : cell;
      if (destinationCell && this.isFocusable(destinationCell)) {
        destinationCell.focus();
        this.scrollToCellIfNeeded(destinationCell);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowRight" /* ArrowRight */ || e.key === "ArrowDown" /* ArrowDown */) {
      const cell = e.target.closest(this.selectors.Cell);
      if (!cell) {
        this.debugLog("cellNavigation", "cell not found");
        return;
      }
      const focusableWidgets = [
        ...cell.querySelectorAll(this.selectors.Focusable)
      ];
      const widgetIdx = focusableWidgets.findIndex((el) => el === e.target);
      const nextFocusable = widgetIdx === focusableWidgets.length - 1 ? 0 : widgetIdx + 1;
      const widgetToFocus = focusableWidgets[nextFocusable];
      if (this.isFocusable(widgetToFocus)) {
        widgetToFocus.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" /* ArrowLeft */ || e.key === "ArrowUp" /* ArrowUp */) {
      const cell = e.target.closest(this.selectors.Cell);
      if (!cell) {
        this.debugLog("cellNavigation", "cell not found");
        return;
      }
      const focusableWidgets = [
        ...cell.querySelectorAll(this.selectors.Focusable)
      ];
      const widgetIdx = focusableWidgets.findIndex((el) => el === e.target);
      const previousFocusable = widgetIdx === 0 ? focusableWidgets.length - 1 : widgetIdx - 1;
      const widgetToFocus = focusableWidgets[previousFocusable];
      if (this.isFocusable(widgetToFocus)) {
        widgetToFocus.focus();
      }
      return;
    }
  }
  /**
   * Handles the navigation outside a cell
   * on the grid level
   */
  gridNavigation(e) {
    const { target } = e;
    if (!(e.target instanceof Element)) return;
    if (!(target instanceof Element)) return;
    if (this.keys.length <= 2) {
      if (e.key === "Enter" /* Enter */) {
        const currentCell = e.target;
        if (currentCell.getAttribute?.("data-disabled") === "true") {
          const row = currentCell.closest(this.selectors.Row);
          if (row) {
            const cellPosition = this.getColumnIndex(currentCell);
            if (cellPosition !== void 0) {
              const destRow = e.shiftKey ? this.getPreviousRow(row) : this.getNextRow(row);
              const destinationCell = destRow ? this.getVisibleCellFromRow(destRow, cellPosition) : currentCell;
              if (destinationCell && this.isFocusable(destinationCell)) {
                destinationCell.focus();
                this.scrollToCellIfNeeded(destinationCell);
                e.preventDefault();
              }
            }
          }
          return;
        }
        const cell = e.target.querySelector(this.selectors.Focusable);
        if (cell && this.isFocusable(cell)) {
          cell.focus();
          e.preventDefault();
        }
      }
      if (e.key === "Tab" /* Tab */) {
        const direction = e.shiftKey ? "prev" : "next";
        const cell = e.target.closest(this.selectors.Cell);
        if (cell && cell instanceof Element) {
          const nextCell = this.findUntil(direction, cell, this.selectors.Cell);
          if (nextCell && this.isFocusable(nextCell)) {
            nextCell.focus();
            this.scrollToCellIfNeeded(nextCell);
            e.preventDefault();
          }
        }
        return;
      }
      if (e.key === "ArrowLeft" /* ArrowLeft */ || e.key === "ArrowRight" /* ArrowRight */) {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        const direction = e.key === "ArrowLeft" /* ArrowLeft */ ? "prev" : "next";
        const cell = e.target.closest(this.selectors.Cell);
        if (cell && cell instanceof Element) {
          const closeFocusable = this.findUntil(
            direction,
            cell,
            this.selectors.Cell
          );
          if (closeFocusable) {
            closeFocusable.focus();
            this.scrollToCellIfNeeded(closeFocusable);
          }
        }
      }
      if (e.key === "ArrowDown" /* ArrowDown */ || e.key === "ArrowUp" /* ArrowUp */) {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        this.verticalCellNavigation(e);
        return;
      }
      if (e.key === "PageUp" /* PageUp */ || e.key === "PageDown" /* PageDown */) {
        this.pageCellNavigation(e);
        return;
      }
      if (e.key === "Home" /* Home */ || e.key === "End" /* End */) {
        const row = e.target.closest(this.selectors.Row);
        const rowChildren = [...row?.children || []];
        if (e.key === "End") rowChildren.reverse();
        this.focusOnFirstCell(rowChildren);
      }
    } else {
      const [firstKey, secondKey] = this.keys;
      if (firstKey === "Control" && (secondKey === "Home" /* Home */ || secondKey === "End" /* End */)) {
        const row = e.target.closest(this.selectors.Row);
        const siblings = row.parentElement?.children;
        if (!siblings) {
          this.debugLog("cellNavigation", "siblings not found");
          return;
        }
        const rowToFocus = secondKey === "Home" /* Home */ ? siblings[0] : siblings[siblings.length - 1];
        const rowChildren = [...rowToFocus?.children || []];
        if (secondKey === "End" /* End */) rowChildren.reverse();
        this.focusOnFirstCell(rowChildren);
      }
    }
  }
  pageCellNavigation(e) {
    if (!(e.target instanceof Element)) return;
    const row = e.target.closest(this.selectors.Row);
    const cell = e.target.closest(this.selectors.Cell);
    if (row && cell) {
      const position = this.getColumnIndex(cell);
      if (position === void 0) {
        this.debugLog("cellNavigation", "position not found");
        return;
      }
      const direction = e.key === "PageUp" /* PageUp */ ? "prev" : "next";
      const siblings = row.parentElement?.children;
      if (!siblings) {
        this.debugLog("cellNavigation", "siblings not found");
        return;
      }
      let destinationRow;
      if (this.pageUpDown) {
        const methodClbk = direction === "prev" ? "previousSibling" : "nextSibling";
        let sibling = row[methodClbk];
        if (sibling === null) return;
        let lastVisitedSibling = sibling;
        for (let i = 0; i < this.pageUpDown - 1 && sibling; i++) {
          sibling = sibling[methodClbk];
          if (sibling) {
            lastVisitedSibling = sibling;
          }
        }
        destinationRow = sibling ? sibling : lastVisitedSibling;
      } else {
        const navigableRows = Array.from(siblings).filter(
          (sibling) => !this.isNavSkipped(sibling)
        );
        destinationRow = direction === "prev" ? navigableRows[0] : navigableRows[navigableRows.length - 1];
      }
      if (!destinationRow || !(destinationRow instanceof Element)) return;
      const targetCell = this.getVisibleCellFromRow(destinationRow, position);
      if (targetCell && this.isFocusable(targetCell)) {
        targetCell.focus();
        this.scrollToCellIfNeeded(targetCell);
      }
    }
  }
  verticalCellNavigation(e) {
    if (!(e.target instanceof Element)) return;
    const row = e.target.closest(this.selectors.Row);
    const cell = e.target.closest(this.selectors.Cell);
    if (!row || !cell) return;
    const cellPosition = this.getColumnIndex(cell);
    if (cellPosition === void 0) {
      this.debugLog("verticalCellNavigation", "cell position not found");
      return;
    }
    const direction = e.key === "ArrowUp" /* ArrowUp */ ? "prev" : "next";
    const destinationRow = this.findUntil(direction, row, this.selectors.Row) ?? this.getRowInAdjacentRowGroup(row, direction);
    if (!destinationRow) return;
    this.focusCellInRow(destinationRow, cellPosition);
  }
  /**
   * Sending a row `Element` and then the first cell will be focused.
   *
   * If you want to focus the last cell then the row children can be passed in
   * reversed order
   */
  focusOnFirstCell(el) {
    for (let i = 0; i < el.length; i++) {
      const child = el[i];
      if (this.isHiddenCell(child)) continue;
      if (this.isFocusable(child)) {
        child.focus();
        this.scrollToCellIfNeeded(child);
        return;
      }
    }
  }
  /**
   * Returns the next navigable row (same rowgroup, or the first navigable
   * row of the next rowgroup), skipping data-nav-skip rows, or null.
   */
  getNextRow(row) {
    return this.findUntil("next", row, this.selectors.Row) ?? this.getRowInAdjacentRowGroup(row, "next");
  }
  /**
   * Returns the previous navigable row (same rowgroup, or the last navigable
   * row of the previous rowgroup), skipping data-nav-skip rows, or null.
   */
  getPreviousRow(row) {
    return this.findUntil("prev", row, this.selectors.Row) ?? this.getRowInAdjacentRowGroup(row, "prev");
  }
  /**
   * Get the column index of a `cell` based on the first `row` parent.
   * `cellIndex` could be used, but it's not supported in HTML tables.
   */
  getColumnIndex(cell) {
    let position = 0;
    const siblings = cell?.parentNode?.children;
    if (!siblings) return void 0;
    while (cell !== siblings[position] && siblings[position] !== void 0) {
      position++;
    }
    if (siblings[position] === void 0) {
      this.debugLog("getColumnIndex", "position finding was not successful");
      return void 0;
    }
    return position;
  }
  /**
   * Equivalent to prevUntil/nextUntil in jQuery
   * https://api.jquery.com/prevUntil/
   */
  // eslint-disable-next-line max-params
  findUntil(direction, el, matchSelector, exitSelector) {
    let element = el;
    const method = direction === "next" ? "nextSibling" : "previousSibling";
    while (element[method]) {
      const sibling = element[method];
      if (!sibling) return null;
      if (exitSelector && sibling instanceof Element && sibling.matches(exitSelector)) {
        return null;
      }
      if (sibling instanceof Element && sibling.matches(matchSelector)) {
        if (!this.isHiddenCell(sibling) && !this.isNavSkipped(sibling) && !this.isDisabledCell(sibling)) {
          return sibling;
        }
      }
      element = sibling;
    }
    return null;
  }
  /**
   * Public API: scrolls to a cell if it's hidden under pinned columns or outside viewport.
   * Use when focusing a cell programmatically (e.g. from validation popover).
   */
  scrollToCell(cell) {
    this.scrollToCellIfNeeded(cell);
  }
  /**
   * Scrolls to a cell if it's hidden under pinned columns
   */
  scrollToCellIfNeeded(cell) {
    if (!(cell instanceof HTMLElement)) return;
    const tableScroller = cell.closest(
      ".c-editor-table-scroller"
    );
    if (!tableScroller) return;
    requestAnimationFrame(() => {
      const cellRect = cell.getBoundingClientRect();
      const scrollerRect = tableScroller.getBoundingClientRect();
      const table = cell.closest("table");
      if (!table) return;
      const firstRow = table.querySelector("tr");
      if (!firstRow) return;
      const pinnedLeftCells = firstRow.querySelectorAll(
        '.c-editor-table-pinned-cell[style*="left"]'
      );
      let pinnedLeftWidth = 0;
      pinnedLeftCells.forEach((pinnedCell) => {
        if (pinnedCell instanceof HTMLElement) {
          const leftValue = pinnedCell.style.left;
          if (leftValue) {
            const left = parseFloat(leftValue);
            const width = pinnedCell.offsetWidth;
            pinnedLeftWidth = Math.max(pinnedLeftWidth, left + width);
          }
        }
      });
      const pinnedRightCells = firstRow.querySelectorAll(
        '.c-editor-table-pinned-cell[style*="right"]'
      );
      let pinnedRightWidth = 0;
      pinnedRightCells.forEach((pinnedCell) => {
        if (pinnedCell instanceof HTMLElement) {
          const rightValue = pinnedCell.style.right;
          if (rightValue) {
            const right = parseFloat(rightValue);
            const width = pinnedCell.offsetWidth;
            pinnedRightWidth = Math.max(pinnedRightWidth, right + width);
          }
        }
      });
      const cellLeftRelative = cellRect.left - scrollerRect.left;
      const cellRightRelative = cellLeftRelative + cellRect.width;
      const scrollerWidth = scrollerRect.width;
      if (cellLeftRelative < pinnedLeftWidth) {
        const cellAbsoluteLeft = tableScroller.scrollLeft + cellLeftRelative;
        const targetScrollLeft = cellAbsoluteLeft - pinnedLeftWidth - 10;
        tableScroller.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth"
        });
        return;
      }
      if (
        // pinnedRightWidth > 0 &&
        cellRightRelative > scrollerWidth - pinnedRightWidth
      ) {
        const cellAbsoluteRight = tableScroller.scrollLeft + cellRightRelative;
        const targetScrollLeft = cellAbsoluteRight - (scrollerWidth - pinnedRightWidth) + 10;
        tableScroller.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth"
        });
        return;
      }
      if (cellRightRelative > scrollerWidth) {
        const cellAbsoluteRight = tableScroller.scrollLeft + cellRightRelative;
        const targetScrollLeft = cellAbsoluteRight - scrollerWidth + 10;
        tableScroller.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth"
        });
        return;
      }
      if (cellLeftRelative < 0) {
        const cellAbsoluteLeft = tableScroller.scrollLeft + cellLeftRelative;
        tableScroller.scrollTo({
          left: Math.max(0, cellAbsoluteLeft - 10),
          // 10px padding
          behavior: "smooth"
        });
      }
    });
  }
};
export {
  DataGridNav,
  useTableNav
};
//# sourceMappingURL=index.mjs.map